import cx from 'classnames';
import { GatsbyImageProps } from 'gatsby-plugin-image';
import React, { useState } from 'react';
import {
	Accordion,
	AccordionItem,
	AccordionItemButton,
	AccordionItemHeading,
	AccordionItemPanel,
} from 'react-accessible-accordion';

import { Button, Image } from '../../index';
import { Chevron, Cross, Download, LinkedIn, Windowed } from '../Icon/index';
import * as styles from './Person.module.css';

const Person = ({
	image,
	hiresimage,
	name,
	title,
	categoryHeading,
	children,
	cv,
	downloadButton,
	isCampaign,
	link,
}: {
	image?: GatsbyImageProps;
	hiresimage?: string;
	name: string;
	title: string;
	categoryHeading?: string;
	cv?: string;
	downloadButton?: React.ReactNode;
	children?: React.ReactNode;
	isCampaign?: boolean;
	link?: string;
}) => {
	const [modalOpen, setModalOpen] = useState(false);

	return (
		<div className={cx(styles.person, { [styles.noimage]: !image })}>
			<PersonCard
				image={image}
				name={name}
				title={title}
				cv={cv}
				link={link}
				isCampaign={isCampaign}
				setModalOpen={setModalOpen}
			/>
			{!cv && !isCampaign && (
				<>
					<DropDown bio={children} downloadButton={downloadButton} />
					<Modal
						modalOpen={modalOpen}
						setModalOpen={setModalOpen}
						heading={categoryHeading}
						name={name}
						jobtitle={title}
						image={image}
						hiresimage={hiresimage}
						bio={children}
						tag="Tracsis plc"
						downloadButton={downloadButton}
					/>
				</>
			)}
		</div>
	);
};

const PersonCard = ({
	image,
	name,
	title,
	cv,
	setModalOpen,
	isCampaign,
	link,
}: {
	image: GatsbyImageProps;
	name: string;
	title: string;
	cv?: string;
	isCampaign?: boolean;
	link?: string;
	setModalOpen: (value: boolean | ((prevVar: boolean) => boolean)) => void;
}) => {
	return (
		<div className={isCampaign ? styles.innotransCard : styles.card}>
			{image ? (
				<div className={ isCampaign ? styles.innotransImage : styles.image }>
					<Image image={image} alt={name} />
				</div>
			) : null }

			<div className={styles.content}>
				{image ? (
					<div>
						<h5 className={styles.name}>{name}</h5>
						<h6 className={styles.title}>{title}</h6>
					</div>
				) : (
					// for innotrans 2024
					<div className={styles.isCampaign}>
						<h5 className={styles.name}>{name}</h5>
						<h6 className={styles.title}>{title}</h6>
					</div>
				)}

				<div className={styles.buttons}>
					{cv ? (
						<a
							href={cv}
							download
							className={cx(styles.button, styles.absolute)}>
							See attached CV
							<Download />
						</a>
					) : isCampaign ? (
						// for innotrans 2024
						<div className={styles.buttons}>
							<a href={link} target="_blank">
								<button
									className={cx(
										styles.innotransButton,
										styles.absolute
									)}>
									<LinkedIn className={styles.socialIcon} />
								</button>
							</a>
						</div>
					) : (
						<button
							className={cx(styles.button, styles.absolute, styles.dropdown)}
							onClick={() => setModalOpen(true)}>
							Read Bio
							<Windowed />
						</button>
					)}
				</div>
			</div>
		</div>
	);
};

const DropDown = ({
	downloadButton,
	bio,
}: {
	downloadButton: React.ReactNode;
	bio: React.ReactNode;
}) => {
	const [accordionOpen, setAccordionOpen] = useState(false);

	return (
		<Accordion
			allowZeroExpanded
			onChange={() => setAccordionOpen(!accordionOpen)}
			className={styles.accordion}>
			<AccordionItem>
				<AccordionItemHeading>
					<AccordionItemButton className={cx(styles.button, styles.absolute)}>
						Read Bio
						<div
							className={cx(styles.chevron, {
								[styles.active]: accordionOpen,
							})}>
							<Chevron />
						</div>
					</AccordionItemButton>
				</AccordionItemHeading>
				<AccordionItemPanel className={styles.accordionPanel}>
					{bio}

					{downloadButton && downloadButton}
				</AccordionItemPanel>
			</AccordionItem>
		</Accordion>
	);
};

const Modal = ({
	modalOpen,
	setModalOpen,
	name,
	jobtitle,
	heading,
	image,
	hiresimage,
	bio,
	tag,
	downloadButton,
}: {
	modalOpen: boolean;
	setModalOpen: (value: boolean | ((prevVar: boolean) => boolean)) => void;
	name: string;
	jobtitle: string;
	heading: string;
	image?: GatsbyImageProps;
	hiresimage?: string;
	bio: React.ReactNode;
	tag: string;
	downloadButton?: React.ReactNode;
}) => {
	return (
		<div className={cx(styles.overlay, { [styles.active]: modalOpen })}>
			<div className={styles.modal}>
				<div className={styles.top}>
					<h5 className={styles.heading}>{heading}</h5>

					<button className={styles.button} onClick={() => setModalOpen(false)}>
						Close
						<Cross />
					</button>
				</div>

				<div className={styles.metadata}>
					<div className={cx(styles.image, styles.large)}>
						<Image image={image} alt={name} />
					</div>
					<div className={styles.info}>
						<div>
							<h5 className={styles.name}>{name}</h5>
							<h6 className={styles.title}>{jobtitle}</h6>
						</div>

						<p className={styles.tag}>{tag}</p>

						{hiresimage && (
							<Button to={hiresimage}>
								Download Hi-Res Photo <Download />
							</Button>
						)}
					</div>
				</div>

				<div className={styles.bio}>
					<p>{bio}</p>

					{downloadButton && downloadButton}
				</div>
			</div>
		</div>
	);
};

export default Person;
